<script lang="ts" setup>
import WhyChooseNft from '~/assets/images/nft-tickets/why-choose-nft.svg'
import WhyChoosePayment from '~/assets/images/nft-tickets/why-choose-payment.svg'
import WhyChooseSignUp from '~/assets/images/nft-tickets/why-choose-sign-up.svg'
import WhyChooseWallets from '~/assets/images/nft-tickets/why-choose-wallets.svg'
import WhyChooseSocial from '~/assets/images/nft-tickets/why-choose-social.svg'
import type { StrapiBlog } from '~~/types/strapi'

const { $i18n } = useNuxtApp()
const { t, locale } = useI18n()
const localeRoute = useLocaleRoute()
const chooseItems = [
  {
    id: '0',
    image: WhyChooseNft,
    head: t('seamless-nft-ticketing'),
    description: t('no-hot-crypto-wallet'),
  },
  {
    id: '1',
    image: WhyChoosePayment,
    head: t('inclusive-payment-options'),
    description: t('dont-worry-about-crypto'),
  },
  {
    id: '2',
    image: WhyChooseSignUp,
    head: t('easy-sign-up'),
    description: t('sign-up-with-your-apple'),
  },
  {
    id: '3',
    image: WhyChooseWallets,
    head: t('multiple-wallets-networks'),
    description: t('for-crypto-enthusiasts'),
  },
  {
    id: '4',
    image: WhyChooseSocial,
    head: t('engaging-social-features'),
    description: t('each-nft-ticket-comes'),
  },
]

definePageMeta({
  layout: 'menu',
})

const {
  public: { siteUrl },
} = useRuntimeConfig()

useSeoMeta({
  title: t('online-nft-ticketing'),
  description: t('dive-into-the-web'),
  ogTitle: t('online-nft-ticketing'),
  ogDescription: t('dive-into-the-web'),
  ogImage: `${siteUrl}/images/nft-tickets/snippet_screen.jpg`,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/jpeg',
  ogType: 'website',
  twitterTitle: t('online-nft-ticketing'),
  twitterDescription: t('dive-into-the-web'),
  twitterImage: `${siteUrl}/images/nft-tickets/snippet_screen.jpg`,
  twitterCard: 'summary_large_image',
})

const queryData = computed(() => ({
  locale: $i18n.locale.value,
}))

const { find } = useStrapi()

const { data: pageData } = await useAsyncData(
  `blog:${locale.value}`,
  () =>
    find<StrapiBlog>(`blogs`, {
      locale: queryData.value.locale,
      sort: ['date:desc', 'publishedAt:desc', 'updatedAt:desc'],
      pagination: {
        page: 1,
        pageSize: 100,
      },
      populate: ['image'],
    }),
  {
    watch: [queryData],
    immediate: true,
  }
)

const selectedSlugs: string[] = [
  'introducing-nft-tickets-the-future-of-ticketing-for-events-concerts-and-more',
  'nft-tickets-made-easy-how-belong-simplifies-the-process-and-revolutionizes-the-event-industry',
  'the-evolution-of-nft-tickets-a-look-into-the-past-present-and-future',
]
</script>

<template>
  <div class="wrapper">
    <section>
      <div
        class="header flex flex-col items-center relative overflow-hidden lg:flex-row lg:justify-center lg:items-start"
      >
        <div
          class="flex flex-col-reverse items-center w-full mx-5 lg:flex-row lg:justify-between lg:max-w-280.5"
        >
          <div class="relative z-1 mt-55.5 lg:mt-0 max-w-155 lg:max-w-auto">
            <div
              class="font-light text-lg tracking-wider text-center text-[#7540EE] lg:text-2xl lg:text-left lg:mt-20.5"
            >
              {{ t('welcome-to-belong') }}
            </div>
            <h1
              class="mt-68 text-lg text-center lg:text-left w-56.5 mx-auto lg:mt-2 lg:text-[1.75rem] lg:font-light lg:leading-normal lg:w-auto lg:mx-0"
            >
              {{ t('create-nft-tickets-mint') }}
            </h1>
            <div
              class="text-[#CECECE] mt-5.5 text-center mx-5 lg:mt-9 lg:text-left lg:mx-0"
            >
              {{ t('experience-the-future') }}
            </div>
          </div>
          <div class="absolute top-28 z-0 lg:relative lg:top-0 lg:mt-16">
            <NuxtImg
              loading="lazy"
              :alt="t('revolutionizing-event-ticketing')"
              class="w-65.5 h-136 object-contain lg:w-80.5 lg:h-165.5"
              src="/images/nft-tickets/header-phone.webp"
            />
          </div>
        </div>
        <NuxtImg
          loading="lazy"
          :alt="t('welcome-to-belong')"
          class="w-full h-full absolute top-0 object-cover -z-1"
          src="/images/nft-tickets/header-image.webp"
        />
        <div
          class="header-container-hexagons absolute left-0 z-2 hidden lg:block"
        >
          <div
            class="header-container-hexagons-row inline-flex -ml-12.5 -mt-8 overflow-hidden even:-ml-0.25"
            v-for="i in 9"
            :key="i"
          >
            <div
              v-for="i in 100"
              :key="i"
              class="header-container-hexagon relative w-25 h-27.5 bg-[#111] m-0.25 opacity-0 transition-duration-1000 hover:opacity-10 hover:bg-[#0f0] hover:transition-duration-0"
            />
          </div>
        </div>
      </div>
    </section>
    <section>
      <div
        class="flex flex-col items-center mt-10 mx-5 lg:flex-row lg:justify-center lg:mt-16.5"
      >
        <div class="relative">
          <NuxtImg
            class="w-full object-cover object-top hidden lg:flex lg:w-79 lg:h-133.5 lg:object-cover"
            loading="lazy"
            :alt="t('our-innovative-mobile')"
            src="/images/nft-tickets/innovative-mobile.webp"
          />
          <NuxtImg
            class="w-93.875 h-91.5 object-contain lg:hidden"
            loading="lazy"
            :alt="t('our-innovative-mobile')"
            src="/images/nft-tickets/innovative-mobile-2.webp"
          />
        </div>
        <div
          class="mt-15 text-center lg:ml-43.5 max-w-128 lg:max-w-auto lg:mt-0 lg:text-left"
        >
          {{ t('our-innovative-mobile') }}
        </div>
      </div>
      <div class="bg-gray-500 w-full h-0.25 mt-17.5" />
    </section>
    <section>
      <div class="flex flex-col items-center mt-9.5 mx-5 lg:mx-0">
        <div
          class="text-center text-2xl leading-normal tracking-wide lg:max-w-122.5 lg:mx-auto lg:text-[1.75rem]"
        >
          {{ t('belong-makes-it') }}
          <span class="text-[#7540EE]">{{ t('web2-users') }}</span>
          {{ t('to-benefit-from') }}
          <span class="text-[#7540EE]">{{ t('nft-tickets-2') }}</span>
        </div>

        <NuxtImg
          src="/images/nft-tickets/web2-benefits-nft-ticket.png"
          alt=""
          loading="lazy"
          format="webp"
          class="max-w-279 w-full mt-9 object-cover hidden lg:flex lg:px-5 xl:px-0"
        />

        <NuxtImg
          src="/images/nft-tickets/web2-benefits-nft-ticket-mobile.png"
          alt=""
          loading="lazy"
          format="webp"
          class="max-w-97 w-full mt-9 object-cover lg:hidden"
        />
      </div>
      <div class="bg-gray-500 w-full h-0.25 mt-23.5" />
    </section>
    <section>
      <WhyBelong
        :data="chooseItems"
        :title="t('why-choose-belong')"
        class="my-10"
      />
      <div class="bg-gray-500 w-full h-0.25" />
    </section>
    <section>
      <div
        class="flex flex-col-reverse items-center mt-9 px-5 lg:flex-row lg:justify-between lg:mt-13.5 lg:max-w-285.5 lg:mx-auto xl:px-0"
      >
        <div class="mt-1.5 lg:mt-14.5">
          <div
            class="text-center text-2xl text-[#8660C2] lg:text-[1.75rem] lg:text-left lg:leading-normal lg:tracking-wide"
          >
            {{ t('product-and-features') }}
          </div>
          <div
            class="text-center mt-9 text-[#CECECE] max-w-130.25 lg:mt-5.5 lg:text-left"
          >
            <NuxtLink
              :to="localeRoute('/')"
              class="bg-black w-30 h-8 inline-flex text-center tracking-wide rounded-[20px] items-center justify-center text-[#FFB0F0] border border-[#7540ee80] -ml-2.75"
              >Belong.net</NuxtLink
            >
            {{ t('s-nft-based-system') }}
          </div>
        </div>
        <div class="product-and-features-image relative lg:ml-5">
          <NuxtImg
            loading="lazy"
            :alt="t('product-and-features')"
            src="/images/nft-tickets/product-and-features.webp"
            class="object-cover w-140.5 h-122.5"
          />
        </div>
      </div>
      <div class="bg-gray-500 w-full h-0.25 mt-14 lg:mt-11" />
    </section>
    <section>
      <div
        class="flex flex-col items-center px-5 mt-19.5 lg:flex-row lg:justify-between lg:mx-auto lg:max-w-246.25"
      >
        <div class="relative security-image">
          <NuxtImg
            loading="lazy"
            :alt="t('security')"
            class="h-63.25 object-top w-75 lg:h-99 object-cover lg:object-center"
            src="/images/nft-tickets/security.webp"
          />
        </div>
        <div class="mt-10 lg:mt-0">
          <div
            class="text-center text-2xl text-[#72FFCC] lg:text-[1.75rem] lg:text-left lg:leading-normal lg:tracking-wide"
          >
            {{ t('security') }}
          </div>
          <div
            class="text-center mt-6.5 max-w-130 lg:max-w-auto lg:mt-9 lg:text-left"
          >
            {{ t('rest-assured') }}
            <NuxtLink
              :to="localeRoute('/')"
              class="bg-black w-30 h-8 inline-flex text-center tracking-wide rounded-[20px] items-center justify-center text-[#FFB0F0] border border-[#7540ee80]"
              >Belong.net</NuxtLink
            >
            {{ t('s-platform-has-passed') }}
          </div>
        </div>
      </div>
      <div class="bg-gray-500 w-full h-0.25 mt-11.5 lg:mt-21.5" />
    </section>
    <section>
      <div
        class="flex flex-col-reverse items-center pb-17.5 lg:flex-row lg:justify-between mt-21 lg:max-w-235 lg:mx-auto"
      >
        <div class="px-5 mt-6.5 lg:mt-0 lg:px-0">
          <div
            class="text-center text-2xl text-[#FFC658] lg:text-[1.75rem] lg:text-left lg:leading-normal lg:tracking-wide"
          >
            {{ t('community-engagement') }}
          </div>
          <div
            class="text-center mt-7.5 max-w-130.25 lg:max-w-auto lg:mt-9 lg:text-left"
          >
            <NuxtLink
              :to="localeRoute('/')"
              class="bg-black w-30 h-8 inline-flex text-center tracking-wide rounded-[20px] items-center justify-center text-[#FFB0F0] border border-[#7540ee80] -ml-2.75"
              >Belong.net</NuxtLink
            >
            {{ t('offers-a-complete-solution') }}
          </div>
        </div>
        <NuxtImg
          loading="lazy"
          :alt="t('community-engagement')"
          class="w-67 h-59.5 object-contain md:w-94 md:h-84"
          src="/images/nft-tickets/community-engagement.webp"
        />
      </div>
      <div class="bg-gray-500 w-full h-0.25 mt-5 lg:hidden" />
    </section>

    <section>
      <InstallSection class="px-5 pt-17.5" />
    </section>

    <template v-if="pageData?.data?.length">
      <section>
        <div class="max-w-310 mx-auto pt-17.5">
          <BlogLinks
            :pages="
              pageData.data.filter((entry) =>
                selectedSlugs.includes(entry.slug)
              )
            "
          />
        </div>
      </section>
    </template>
  </div>
</template>

<style scoped>
@reference "tailwindcss";
.header {
  &::after {
    @apply absolute bottom-0 h-115 w-full;
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
  }
}

.header-container-hexagons {
  animation: animate 4s linear infinite;
}
.header-container-hexagon {
  -webkit-mask-image: url('~/assets/images/nft-tickets/hexagon.svg');
  mask-image: url('~/assets/images/nft-tickets/hexagon.svg');
  mask-size: cover;
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
}

.product-and-features-image {
  &::before,
  &::after {
    @apply absolute h-full w-19.75 bottom-0;
    content: '';
  }
  &::before {
    @apply left-0;
    background: linear-gradient(
      90deg,
      #151515 0%,
      #151515 18.75%,
      rgba(21, 21, 21, 0) 100%
    );
  }
  &::after {
    @apply right-0;
    background: linear-gradient(
      270deg,
      #151515 0%,
      #151515 18.75%,
      rgba(21, 21, 21, 0) 100%
    );
  }
}

.security-image {
  &::before {
    @apply absolute bottom-0 w-full h-15.75;
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
  }
}

@keyframes animate {
  0% {
    filter: hue-rotate(0deg);
  }
  100% {
    filter: hue-rotate(270deg);
  }
}

@media (min-width: 375px) {
  .header {
    &::after {
      @apply h-94.5;
    }
  }
}

@media (min-width: 1024px) {
  .header {
    &::after {
      @apply h-29.5;
    }
  }
}
</style>
